import React, { useState, useEffect, useRef } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Form, 
  Button, 
  Alert,
  Modal,
  Table,
  InputGroup,
  Spinner
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faArrowLeft, faCamera, faImage } from '@fortawesome/free-solid-svg-icons';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axiosInstance from 'utils/axiosInterceptors';
import { CarMakeSelect } from 'components/vehicles/CarMakeSelect';
import { CarModelSelect } from 'components/vehicles/CarModelSelect';
import { CarModelBodyTypeSelect, CAR_MODEL_BODY_TYPES } from 'components/vehicles/CarModelBodyTypeSelect';

// Types
enum FuelType {
  Petrol = 1,
  Diesel = 2,
  Electric = 3,
  Hybrid = 4,
  CNG = 5,
  LPG = 6
}

enum Transmission {
  Manual = 1,
  Automatic = 2,
  CVT = 3,
  SemiAutomatic = 4
}

interface CreateVehicleRequest {
  CarMake: string;
  CarModel: string;
  Year: number;
  ChassisNo?: string;
  PlateNumber?: string;
  ConductionNumber?: string;
  Color?: string;
  FuelType?: FuelType;
  Transmission?: Transmission;
  CustomerId?: number;
  BodyType?: string;
}

interface Customer {
  customerId: number;
  customerName: string;
  email: string;
  contactNumber: string;
}

interface ExistingVehicle {
  vehicleId: number;
  plateNumber?: string | null;
  carMake: string;
  carModel: string;
  year: number;
}

interface IdentifyPhotoResponse {
  isVehicle: boolean;
  plateNumber?: string | null;
  carMake?: string | null;
  carModel?: string | null;
  year?: number | null;
  color?: string | null;
  bodyType?: string | null;
  fuelType?: string | null;
  transmission?: string | null;
  summary?: string | null;
  existingVehicle?: ExistingVehicle | null;
}

type PlateStatus = 'idle' | 'checking' | 'available' | 'exists' | 'error';

function namedEnumValue(enumObject: object, name?: string | null) {
  if (!name) return undefined;
  const match = Object.entries(enumObject).find(
    ([key, value]) =>
      Number.isNaN(Number(key)) &&
      key.toLowerCase() === name.toLowerCase() &&
      typeof value === 'number'
  );
  return match ? (match[1] as number) : undefined;
}

function readErrorMessage(error: unknown, fallback: string) {
  if (error && typeof error === 'object' && 'response' in error) {
    const data = (error as {
      response?: { data?: { errorMessage?: string; message?: string } | string };
    }).response?.data;
    if (typeof data === 'string' && data.trim()) return data;
    if (data && typeof data === 'object') {
      if (data.errorMessage) return data.errorMessage;
      if (data.message) return data.message;
    }
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}



const CreateVehicle: React.FC = () => {
  const navigate = useNavigate();
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const plateRequestId = useRef(0);
  
  // Form state
  const [formData, setFormData] = useState<CreateVehicleRequest>({
    CarMake: '',
    CarModel: '',
    Year: new Date().getFullYear(),
    ChassisNo: '',
    PlateNumber: '',
    ConductionNumber: '',
    Color: '',
    FuelType: undefined,
    Transmission: undefined,
    CustomerId: undefined,
    BodyType: ''
  });
  
  // Customer lookup state
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerLoading, setCustomerLoading] = useState(false);
  
  // Form state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [plateStatus, setPlateStatus] = useState<PlateStatus>('idle');
  const [existingVehicle, setExistingVehicle] = useState<ExistingVehicle | null>(null);
  const [identifyingPhoto, setIdentifyingPhoto] = useState(false);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [photoMessage, setPhotoMessage] = useState<{ variant: 'info' | 'warning' | 'danger'; text: string } | null>(null);

  // Debounced search effect
  useEffect(() => {
    if (customerSearch.length > 0) {
      const timer = setTimeout(() => {
        searchCustomers(customerSearch);
      }, 500); // 500ms debounce

      return () => clearTimeout(timer);
    } else {
      setCustomers([]);
    }
  }, [customerSearch]);

  useEffect(() => {
    const plate = formData.PlateNumber?.trim() ?? '';
    if (!plate) {
      setPlateStatus('idle');
      setExistingVehicle(null);
      return;
    }

    setPlateStatus('checking');
    const requestId = ++plateRequestId.current;
    const timer = window.setTimeout(() => {
      axiosInstance
        .get('/api/v1/vehicles/by-plate', { params: { plateNumber: plate } })
        .then(response => {
          if (plateRequestId.current !== requestId) return;
          const vehicle = response.data?.exists ? (response.data.vehicle as ExistingVehicle) : null;
          setExistingVehicle(vehicle);
          setPlateStatus(vehicle ? 'exists' : 'available');
        })
        .catch(() => {
          if (plateRequestId.current !== requestId) return;
          setExistingVehicle(null);
          setPlateStatus('error');
        });
    }, 500);

    return () => window.clearTimeout(timer);
  }, [formData.PlateNumber]);

  useEffect(() => {
    return () => {
      if (photoPreview) URL.revokeObjectURL(photoPreview);
    };
  }, [photoPreview]);

  // Search customers
  const searchCustomers = async (search: string) => {
    try {
      setCustomerLoading(true);
      const response = await axiosInstance.get(`/api/v1/customers?search=${encodeURIComponent(search)}&pageSize=50`);
      setCustomers(response.data); // API returns direct array, not wrapped in data property
    } catch (error) {
      console.error('Error searching customers:', error);
    } finally {
      setCustomerLoading(false);
    }
  };

  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'Year' ? parseInt(value) || 0 : 
             name === 'FuelType' || name === 'Transmission' ? (value ? parseInt(value) : undefined) : 
             name === 'PlateNumber' ? value.toUpperCase() :
             value
    }));
  };

  // Handle customer selection
  const handleCustomerSelect = (customer: Customer) => {
    setSelectedCustomer(customer);
    setFormData(prev => ({
      ...prev,
      CustomerId: customer.customerId
    }));
    setShowCustomerModal(false);
    setCustomerSearch('');
    setCustomers([]);
  };

  const identifyPhoto = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      setPhotoMessage({ variant: 'warning', text: 'Choose a photo of the vehicle.' });
      return;
    }

    setPhotoPreview(current => {
      if (current) URL.revokeObjectURL(current);
      return URL.createObjectURL(file);
    });
    setIdentifyingPhoto(true);
    setPhotoMessage(null);
    setSubmitError(null);

    try {
      const body = new FormData();
      body.append('file', file);
      const response = await axiosInstance.post<IdentifyPhotoResponse>('/api/v1/vehicles/identify-photo', body);
      const data = response.data;

      if (data.existingVehicle?.vehicleId) {
        toast.info('This plate is already saved. Opening that vehicle.');
        navigate(`/vehicles/${data.existingVehicle.vehicleId}`);
        return;
      }

      setFormData(prev => ({
        ...prev,
        PlateNumber: data.plateNumber?.trim() ? data.plateNumber.toUpperCase() : prev.PlateNumber,
        CarMake: data.carMake?.trim() || prev.CarMake,
        CarModel: data.carModel?.trim() || prev.CarModel,
        Year: data.year || prev.Year,
        Color: data.color?.trim() || prev.Color,
        BodyType: data.bodyType && CAR_MODEL_BODY_TYPES.some(type => type.value === data.bodyType)
          ? data.bodyType
          : prev.BodyType,
        FuelType: (namedEnumValue(FuelType, data.fuelType) as FuelType | undefined) ?? prev.FuelType,
        Transmission: (namedEnumValue(Transmission, data.transmission) as Transmission | undefined) ?? prev.Transmission
      }));

      if (!data.isVehicle && !data.plateNumber) {
        setPhotoMessage({
          variant: 'warning',
          text: data.summary || 'This photo does not look like a vehicle. Try another picture, or enter the plate.'
        });
      } else if (!data.plateNumber) {
        setPhotoMessage({
          variant: 'info',
          text: 'Could not read a plate in that photo. Enter it above. Other details were filled in where the photo was clear.'
        });
      } else {
        setPhotoMessage({
          variant: 'info',
          text: data.summary
            ? `From the photo: ${data.summary}. This plate is not on file yet, so you can continue.`
            : 'Filled in from the photo. This plate is not on file yet, so you can continue.'
        });
      }
    } catch (error: unknown) {
      setPhotoMessage({
        variant: 'danger',
        text: readErrorMessage(error, 'Could not read that photo. Try again or enter the plate.')
      });
    } finally {
      setIdentifyingPhoto(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (plateStatus === 'checking') {
      setSubmitError('Still checking this plate number.');
      return;
    }

    if (plateStatus === 'exists') {
      setSubmitError('This plate is already on file. Open the existing vehicle instead of creating a new one.');
      return;
    }
    
    // Validate that at least one of PlateNumber or ConductionNumber is provided
    if (!formData.PlateNumber?.trim() && !formData.ConductionNumber?.trim()) {
      setSubmitError('Either Plate Number or Conduction Number is required');
      return;
    }
    
    setIsSubmitting(true);
    setSubmitError(null);
    
    try {
      // Filter out empty optional fields
      const submitData = { ...formData };
      if (!submitData.ChassisNo?.trim()) {
        delete submitData.ChassisNo;
      }
      if (!submitData.PlateNumber?.trim()) {
        delete submitData.PlateNumber;
      }
      if (!submitData.ConductionNumber?.trim()) {
        delete submitData.ConductionNumber;
      }
      if (!submitData.Color?.trim()) {
        delete submitData.Color;
      }
      if (!submitData.FuelType) {
        delete submitData.FuelType;
      }
      if (!submitData.Transmission) {
        delete submitData.Transmission;
      }
      if (!submitData.BodyType?.trim()) {
        delete submitData.BodyType;
      }
      if (!submitData.CustomerId) {
        delete submitData.CustomerId;
      }
      
      await axiosInstance.post('/api/v1/vehicles/', submitData);
      
      setSubmitSuccess(true);
      // Reset form
      setFormData({
        CarMake: '',
        CarModel: '',
        Year: new Date().getFullYear(),
        ChassisNo: '',
        PlateNumber: '',
        ConductionNumber: '',
        Color: '',
        FuelType: undefined,
        Transmission: undefined,
        CustomerId: undefined,
        BodyType: ''
      });
      setSelectedCustomer(null);
      
      // Redirect after success
      setTimeout(() => {
        navigate('/vehicles');
      }, 2000);
      
    } catch (error: unknown) {
      console.error('Submit error:', error);
      
      let errorMessage = 'An error occurred while creating the vehicle';
      
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { 
          response?: { 
            status?: number; 
            data?: { 
              message?: string; 
              errors?: Record<string, string[]>; 
            } | string; 
          }; 
          message?: string; 
        };
        
        if (axiosError.response?.status === 400) {
          // Handle 400 Bad Request errors
          if (typeof axiosError.response.data === 'object' && axiosError.response.data?.message) {
            errorMessage = axiosError.response.data.message;
          } else if (typeof axiosError.response.data === 'object' && axiosError.response.data?.errors) {
            // Handle validation errors
            const errors = axiosError.response.data.errors;
            errorMessage = Object.values(errors).flat().join(', ');
          } else if (typeof axiosError.response.data === 'string') {
            errorMessage = axiosError.response.data;
          }
        } else if (typeof axiosError.response?.data === 'object' && axiosError.response.data?.message) {
          errorMessage = axiosError.response.data.message;
        } else if (axiosError.message) {
          errorMessage = axiosError.message;
        }
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      setSubmitError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset success message
  useEffect(() => {
    if (submitSuccess) {
      const timer = setTimeout(() => {
        setSubmitSuccess(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [submitSuccess]);

  return (
    <div>
      <Card>
        <Card.Header>
          <Row className="align-items-center">
            <Col>
              <Button
                variant="link"
                className="p-0 me-2"
                onClick={() => navigate('/vehicles')}
              >
                <FontAwesomeIcon icon={faArrowLeft} />
              </Button>
              <h5 className="mb-0 d-inline">Add New Vehicle</h5>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body>
          {submitError && (
            <Alert variant="danger" dismissible onClose={() => setSubmitError(null)}>
              {submitError}
            </Alert>
          )}
          
          {submitSuccess && (
            <Alert variant="success">
              Vehicle created successfully! Redirecting to vehicles list...
            </Alert>
          )}

          <Form onSubmit={handleSubmit}>
            <Row>
              <Col lg={7}>
                <Form.Group className="mb-3">
                  <Form.Label>Plate Number <small className="text-muted">(or a conduction number below)</small></Form.Label>
                  <Form.Control
                    type="text"
                    name="PlateNumber"
                    value={formData.PlateNumber}
                    onChange={handleInputChange}
                    placeholder="Enter plate number"
                    autoCapitalize="characters"
                    autoCorrect="off"
                    spellCheck={false}
                    autoComplete="off"
                  />
                  {plateStatus === 'checking' && (
                    <Form.Text className="text-muted">Checking this plate...</Form.Text>
                  )}
                  {plateStatus === 'available' && (
                    <Form.Text className="text-success">This plate is not on file yet. You can continue.</Form.Text>
                  )}
                  {plateStatus === 'error' && (
                    <Form.Text className="text-muted">Could not check this plate right now. You can still continue.</Form.Text>
                  )}
                </Form.Group>
              </Col>
              <Col lg={5}>
                <Form.Group className="mb-3">
                  <Form.Label>Photo</Form.Label>
                  <div className="d-flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="primary"
                      disabled={identifyingPhoto}
                      onClick={() => cameraInputRef.current?.click()}
                    >
                      <FontAwesomeIcon icon={faCamera} className="me-2" />
                      Take photo
                    </Button>
                    <Button
                      type="button"
                      variant="outline-secondary"
                      disabled={identifyingPhoto}
                      onClick={() => galleryInputRef.current?.click()}
                    >
                      <FontAwesomeIcon icon={faImage} className="me-2" />
                      Choose photo
                    </Button>
                  </div>
                  <Form.Text className="d-block mt-2">
                    On a phone, Take photo opens the camera. We read the plate and the car, and open the vehicle if that plate is already saved.
                  </Form.Text>
                  {identifyingPhoto && (
                    <div className="d-flex align-items-center text-muted mt-2">
                      <Spinner animation="border" size="sm" className="me-2" />
                      Reading the photo...
                    </div>
                  )}
                  {photoPreview && (
                    <img
                      src={photoPreview}
                      alt="Vehicle"
                      className="rounded border mt-2"
                      style={{ width: 120, height: 90, objectFit: 'cover' }}
                    />
                  )}
                </Form.Group>
                <input
                  ref={cameraInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="d-none"
                  onChange={event => {
                    const file = event.target.files?.[0];
                    event.target.value = '';
                    if (file) void identifyPhoto(file);
                  }}
                />
                <input
                  ref={galleryInputRef}
                  type="file"
                  accept="image/*"
                  className="d-none"
                  onChange={event => {
                    const file = event.target.files?.[0];
                    event.target.value = '';
                    if (file) void identifyPhoto(file);
                  }}
                />
              </Col>
            </Row>

            {existingVehicle && (
              <Alert variant="warning">
                Plate <strong>{existingVehicle.plateNumber || formData.PlateNumber}</strong> is already on{' '}
                {[existingVehicle.carMake, existingVehicle.carModel, existingVehicle.year].filter(Boolean).join(' ')}.
                {' '}
                <Link to={`/vehicles/${existingVehicle.vehicleId}`}>Open this vehicle</Link>
              </Alert>
            )}

            {photoMessage && (
              <Alert variant={photoMessage.variant} dismissible onClose={() => setPhotoMessage(null)}>
                {photoMessage.text}
              </Alert>
            )}

            {plateStatus !== 'exists' && (
            <>
            <Row>
              <Col md={6}>
                <CarMakeSelect
                  value={formData.CarMake}
                  onChange={handleInputChange}
                />
              </Col>
              <Col md={6}>
                <CarModelSelect
                  value={formData.CarModel}
                  carMake={formData.CarMake}
                  onChange={handleInputChange}
                />
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <CarModelBodyTypeSelect
                  value={formData.BodyType}
                  onChange={handleInputChange}
                />
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Year <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="number"
                    name="Year"
                    value={formData.Year}
                    onChange={handleInputChange}
                    required
                    min="1900"
                    max={new Date().getFullYear() + 1}
                    placeholder="Enter year"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Chassis No</Form.Label>
                  <Form.Control
                    type="text"
                    name="ChassisNo"
                    value={formData.ChassisNo}
                    onChange={handleInputChange}
                    placeholder="Enter chassis number"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Conduction Number <small className="text-muted">(required if there is no plate)</small></Form.Label>
                  <Form.Control
                    type="text"
                    name="ConductionNumber"
                    value={formData.ConductionNumber}
                    onChange={handleInputChange}
                    placeholder="Enter conduction number"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Color</Form.Label>
                  <Form.Control
                    type="text"
                    name="Color"
                    value={formData.Color}
                    onChange={handleInputChange}
                    placeholder="Enter vehicle color"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Fuel Type</Form.Label>
                  <Form.Select
                    name="FuelType"
                    value={formData.FuelType || ''}
                    onChange={handleInputChange}
                  >
                    <option value="">Select fuel type</option>
                    <option value={FuelType.Petrol}>Petrol</option>
                    <option value={FuelType.Diesel}>Diesel</option>
                    <option value={FuelType.Electric}>Electric</option>
                    <option value={FuelType.Hybrid}>Hybrid</option>
                    <option value={FuelType.CNG}>CNG</option>
                    <option value={FuelType.LPG}>LPG</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Transmission</Form.Label>
                  <Form.Select
                    name="Transmission"
                    value={formData.Transmission || ''}
                    onChange={handleInputChange}
                  >
                    <option value="">Select transmission</option>
                    <option value={Transmission.Manual}>Manual</option>
                    <option value={Transmission.Automatic}>Automatic</option>
                    <option value={Transmission.CVT}>CVT</option>
                    <option value={Transmission.SemiAutomatic}>Semi-Automatic</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Customer</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="text"
                      readOnly
                      value={selectedCustomer ? selectedCustomer.customerName : ''}
                      placeholder="No customer selected"
                    />
                    <Button
                      variant="outline-secondary"
                      onClick={() => setShowCustomerModal(true)}
                    >
                      Customer Lookup
                    </Button>
                  </InputGroup>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col>
                <div className="d-flex gap-2">
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={isSubmitting || identifyingPhoto || plateStatus === 'checking'}
                  >
                    {isSubmitting ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        Creating...
                      </>
                    ) : (
                      'Create Vehicle'
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => navigate('/vehicles')}
                  >
                    Cancel
                  </Button>
                </div>
              </Col>
            </Row>
            </>
            )}
          </Form>
        </Card.Body>
      </Card>

      {/* Customer Lookup Modal */}
      <Modal show={showCustomerModal} onHide={() => setShowCustomerModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Customer Lookup</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Search Customers</Form.Label>
            <InputGroup>
              <Form.Control
                type="text"
                placeholder="Type to search customers..."
                value={customerSearch}
                onChange={(e) => setCustomerSearch(e.target.value)}
              />
              <InputGroup.Text>
                <FontAwesomeIcon icon={faSearch} />
              </InputGroup.Text>
            </InputGroup>
          </Form.Group>

          {customerLoading && (
            <div className="text-center">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </div>
          )}

          {customers.length > 0 && (
            <Table striped hover responsive>
              <thead>
                <tr>
                  <th>Customer Name</th>
                  <th>Email</th>
                  <th>Contact Number</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {customers.map(customer => (
                  <tr key={customer.customerId}>
                    <td>{customer.customerName}</td>
                    <td>{customer.email}</td>
                    <td>{customer.contactNumber}</td>
                    <td>
                      <Button
                        size="sm"
                        variant="primary"
                        onClick={() => handleCustomerSelect(customer)}
                      >
                        Select
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}

          {customerSearch.length > 0 && customers.length === 0 && !customerLoading && (
            <Alert variant="info">
              No customers found matching your search criteria.
            </Alert>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default CreateVehicle; 